// @flow
import update from 'immutability-helper';
import { ofType, StateObservable } from 'redux-observable';
import { dataURItoBlob } from '../../../../utils/file';
import { uploadImageApi } from '../../api';
import { of } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';
import { setNewAlert } from '../../../alert/duck';
import { endpoints, postProgressCourierApi, postProgressQempoApi, getCouriersApi } from './api';

import type { HTTPError } from '../../../../error';
import type { Country, Courier, Order } from '../../../../entities';
import type { Observable } from 'rxjs';
import type { RootState } from '../../../index';

type DeliverAction = {
  +type: string;
  +error?: HTTPError;
  +order?: Order;
  +orderId?: string;
  +courier?: string;
  +trackingNumber?: string;
  +invoiceURI?: string;
  +invoiceId?: string;
  +couriers?: Courier[];
  +country?: Country;
};

export type DeliverState = {
  +isPostingProgress?: boolean;
  +postProgressSuccessful?: boolean;
  +postProgressError?: HTTPError;
  +isFetchingCouriers?: boolean;
  +couriers?: Courier[];
  +fetchCouriersError?: HTTPError;
};

// Actions
const DELIVER_POST_PROGRESS_COURIER_REQUEST = 'DELIVER_POST_PROGRESS_COURIER_REQUEST';
const DELIVER_POST_PROGRESS_QEMPO_REQUEST = 'DELIVER_POST_PROGRESS_QEMPO_REQUEST';
const DELIVER_POST_PROGRESS_INVOICE_SUCCESS = 'DELIVER_POST_PROGRESS_INVOICE_SUCCESS';
const DELIVER_POST_PROGRESS_SUCCESS = 'DELIVER_POST_PROGRESS_SUCCESS';
const DELIVER_POST_PROGRESS_ERROR = 'DELIVER_POST_PROGRESS_ERROR';
const DELIVER_FETCH_COURIERS_REQUEST = 'DELIVER_FETCH_COURIERS_REQUEST';
const DELIVER_FETCH_COURIERS_SUCCESS = 'DELIVER_FETCH_COURIERS_SUCCESS';
const DELIVER_FETCH_COURIERS_ERROR = 'DELIVER_FETCH_COURIERS_ERROR';

// Action creators
export function postProgressCourier(orderId: string, courier: string, trackingNumber: string, invoiceURI: string): DeliverAction {
  return {
    type: DELIVER_POST_PROGRESS_COURIER_REQUEST,
    orderId,
    courier,
    trackingNumber,
    invoiceURI,
  };
}

export function postProgressCourierInvoiceSuccess(invoiceId: string, orderId: string, courier: string,
                                                  trackingNumber: string): DeliverAction {
  return {
    type: DELIVER_POST_PROGRESS_INVOICE_SUCCESS,
    invoiceId,
    orderId,
    courier,
    trackingNumber,
  };
}

export function postProgressSuccess(order: Order): DeliverAction {
  return {
    type: DELIVER_POST_PROGRESS_SUCCESS,
    order,
  };
}

export function postProgressErrorFn(error: HTTPError): DeliverAction {
  return {
    type: DELIVER_POST_PROGRESS_ERROR,
    error,
  };
}

export function postProgressQempo(orderId: string): DeliverAction {
  return {
    type: DELIVER_POST_PROGRESS_QEMPO_REQUEST,
    orderId,
  };
}

export function fetchCouriers(country: Country): DeliverAction {
  return {
    type: DELIVER_FETCH_COURIERS_REQUEST,
    country,
  };
}

export function fetchCouriersSuccess(couriers: Courier[]): DeliverAction {
  return {
    type: DELIVER_FETCH_COURIERS_SUCCESS,
    couriers,
  };
}

export function fetchCouriersErrorFn(error: HTTPError): DeliverAction {
  return {
    type: DELIVER_FETCH_COURIERS_ERROR,
    error,
  };
}


export const actions = {
  DELIVER_POST_PROGRESS_COURIER_REQUEST,
  DELIVER_POST_PROGRESS_INVOICE_SUCCESS,
  DELIVER_POST_PROGRESS_QEMPO_REQUEST,
  DELIVER_POST_PROGRESS_SUCCESS,
  DELIVER_POST_PROGRESS_ERROR,
  DELIVER_FETCH_COURIERS_REQUEST,
  DELIVER_FETCH_COURIERS_SUCCESS,
  DELIVER_FETCH_COURIERS_ERROR,
  postProgressCourier,
  postProgressCourierInvoiceSuccess,
  postProgressQempo,
  postProgressSuccess,
  postProgressErrorFn,
  fetchCouriers,
  fetchCouriersSuccess,
  fetchCouriersErrorFn,
};

export const initialState: DeliverState = {};

// Reducer
export default function reducer(state: DeliverState = initialState, action: DeliverAction): DeliverState {
  switch (action.type) {
    case DELIVER_POST_PROGRESS_COURIER_REQUEST:
    case DELIVER_POST_PROGRESS_QEMPO_REQUEST:
      return update(state, {
        $unset: ['postProgressError', 'postProgressSuccessful'],
        isPostingProgress: { $set: true },
      });
    case DELIVER_POST_PROGRESS_SUCCESS:
      return update(state, {
        $unset: ['isPostingProgress'],
        postProgressSuccessful: { $set: true },
      });
    case DELIVER_POST_PROGRESS_ERROR:
      return update(state, {
        $unset: ['isPostingProgress'],
        postProgressError: { $set: action.error },
      });
    case DELIVER_FETCH_COURIERS_REQUEST:
      return update(state, {
        $unset: ['fetchCouriersError'],
        isFetchingCouriers: { $set: true },
      });
    case DELIVER_FETCH_COURIERS_SUCCESS:
      return update(state, {
        $unset: ['isFetchingCouriers'],
        couriers: { $set: action.couriers },
      });
    case DELIVER_FETCH_COURIERS_ERROR:
      return update(state, {
        $unset: ['isFetchingCouriers'],
        fetchCouriersError: { $set: action.error },
      });
    default:
      return state;
  }
};

// Epics
export function postProgressCourierInvoiceEpic(action$: Observable<DeliverAction>): Observable<DeliverAction> {
  return action$.pipe(
    ofType(DELIVER_POST_PROGRESS_COURIER_REQUEST),
    mergeMap(({ invoiceURI, orderId, courier, trackingNumber }) => {
      const fd = new FormData();
      fd.append('file', dataURItoBlob(invoiceURI));
      fd.append('resourceGroup', 'EXTERNAL_INVOICE');
      return uploadImageApi(fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).pipe(
        mergeMap((imageResponse: { id: string }) => of(
          postProgressCourierInvoiceSuccess(imageResponse.id, orderId, courier, trackingNumber)
        )),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postProgressErrorFn(e),
              setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postProgressErrorFn(e),
              setNewAlert('order', 'error', undefined, 'httpErrors.post.400'),
            );
          } else if (e.code === 413) {
            return of(postProgressErrorFn(e),
              setNewAlert('order', 'error', undefined, 'httpErrors.post.413'),
            );
          }
          return of(postProgressErrorFn(e));
        }));
    }),
  );
}

export function postProgressCourierEpic(action$: Observable<DeliverAction>): Observable<DeliverAction> {
  return action$.pipe(
    ofType(DELIVER_POST_PROGRESS_INVOICE_SUCCESS),
    mergeMap(({ invoiceId, orderId, courier, trackingNumber }) => postProgressCourierApi({
      trackingNumber,
      courierId: parseInt(courier),
      storedFileId: invoiceId,
    }, {
      route: endpoints.POST_PROGRESS_COURIER.replace(':orderId', orderId),
    }).pipe(
      map(postProgressSuccess),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(postProgressErrorFn(e),
            setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(postProgressErrorFn(e),
            setNewAlert('order', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(postProgressErrorFn(e));
      }),
    )),
  );
}

export function postProgressQempoEpic(action$: Observable<DeliverAction>): Observable<DeliverAction> {
  return action$.pipe(
    ofType(DELIVER_POST_PROGRESS_QEMPO_REQUEST),
    mergeMap(({ orderId }) => postProgressQempoApi(undefined, {
      route: endpoints.POST_PROGRESS_QEMPO.replace(':orderId', orderId),
    }).pipe(
      map(postProgressSuccess),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(postProgressErrorFn(e),
            setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(postProgressErrorFn(e),
            setNewAlert('order', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(postProgressErrorFn(e));
      }),
    )),
  );
}

export function fetchCouriersEpic(action$: Observable<DeliverAction>): Observable<DeliverAction> {
  return action$.pipe(
    ofType(DELIVER_FETCH_COURIERS_REQUEST),
    mergeMap(({ country }) => getCouriersApi(undefined, {
        route: endpoints.GET_COURIERS.replace(':country', country)
      }).pipe(
      map((response: Courier[]) => fetchCouriersSuccess(response)),
      catchError((e: HTTPError) => of(fetchCouriersErrorFn(e))))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/order/Sale/deliver/duck.js